import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import RegistrationModal from '../components/RegistrationModal';
import Slider from 'react-slick';
import { services, testimonials } from '../mock';
import { servicePageData } from '../serviceData';
import { CheckCircle2, ArrowRight, Shield } from 'lucide-react';

const ServicePage = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);
  const pageData = servicePageData[serviceId];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  if (!service || !pageData) {
    return <Navigate to="/" replace />;
  }

  const handleGetStarted = (pkg) => {
    setSelectedPackage(pkg || null);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-[#fd5a27]/20 mb-6">
                <Shield className="w-5 h-5 text-[#fd5a27]" />
                <span className="text-sm font-medium text-gray-200">{pageData.hero.badge}</span>
              </div>
              
              <h1 className="text-5xl font-bold text-white mb-6">
                {pageData.hero.title}
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {pageData.hero.description}
              </p>
              <Button 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get Started - From $45
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl">
              <img
                src={pageData.hero.image || "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMHNlcnZpY2VzfGVufDB8fHx8MTc2MzQwMTIxNXww&ixlib=rb-4.1.0&q=85"}
                alt={service.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Service Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-[#fd5a27]/20 border border-slate-700">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="text-[#fd5a27] font-semibold mb-2">ABOUT THIS SERVICE</p>
              <h2 className="text-4xl font-bold text-white mb-6">
                Comprehensive {service.title}
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed text-lg">
                {service.detailedDescription}
              </p>
              
              <ul className="space-y-3">
                {pageData.keyBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#fd5a27] mr-3 flex-shrink-0 mt-1" />
                    <span className="text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2">HOW WE WORK</p>
            <h2 className="text-4xl font-bold text-white">
              Our Simple Three-Step Process
            </h2>
            <p className="text-gray-300 mt-4">
              Our streamlined process makes {service.title.toLowerCase()} simple and stress-free.
            </p>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              {pageData.process.map((step) => (
                <div key={step.step} className="px-2">
                  <div className="bg-slate-900 border border-slate-700 rounded-xl p-8 text-center transition-all duration-300 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#fd5a27] to-[#e04a1f] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-[#fd5a27]/50">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-gray-300">{step.description}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {pageData.process.map((step) => (
              <div key={step.step} className="bg-slate-900 border border-slate-700 rounded-xl p-8 text-center transition-all duration-300 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
                <div className="w-16 h-16 bg-gradient-to-br from-[#fd5a27] to-[#e04a1f] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-[#fd5a27]/50">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why You Need This Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2">WHY YOU NEED THIS</p>
            <h2 className="text-4xl font-bold text-white mb-4">
              Key Benefits of {service.title}
            </h2>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              {pageData.benefits.map((benefit, index) => (
                <div key={index} className="px-2">
                  <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 transition-all duration-300 hover:border-[#fd5a27] hover:shadow-lg hover:shadow-[#fd5a27]/20">
                    <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-gray-300">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageData.benefits.map((benefit, index) => (
              <div key={index} className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 transition-all duration-300 hover:border-[#fd5a27] hover:shadow-lg hover:shadow-[#fd5a27]/20">
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2">PRICING PACKAGES</p>
            <h2 className="text-4xl font-bold text-white">
              Choose Your Package
            </h2>
            <p className="text-gray-300 mt-4">
              Select the package that best fits your needs. All packages include expert support and guidance.
            </p>
          </div>
          
          {pageData.customPricing ? (
            <div className="max-w-4xl mx-auto text-center flex justify-center">
              <div className="bg-slate-900 border border-slate-700 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-white mb-4">{pageData.customPricing.title}</h3>
                <p className="text-gray-300 mb-6">{pageData.customPricing.description}</p>
                <ul className="space-y-3 mb-8">
                  {pageData.customPricing.features.map((feature, index) => (
                    <li key={index} className="flex items-center justify-center text-gray-200">
                      <CheckCircle2 className="w-5 h-5 text-[#fd5a27] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white px-8 py-4 transition-all duration-300"
                >
                  Get a Quote
                </Button>
              </div>
            </div>
          ) : (
            <div className={`grid gap-6 xl:gap-8 pt-12 mx-auto ${
              pageData.pricing.length === 4
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                : pageData.pricing.length === 3
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl'
                  : 'grid-cols-1 sm:grid-cols-2 max-w-3xl'
            }`}>
              {pageData.pricing.map((pkg) => (
                <div key={pkg.id} className="pt-4 w-full">
                  <PricingCard package={pkg} onGetStarted={handleGetStarted} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2">WHY CHOOSE US</p>
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Work With Trademark IP US
            </h2>
            <p className="text-gray-300 max-w-3xl mx-auto">
              Whether you're just starting out or expanding your business, Trademark IP US is your dedicated partner. We offer clear communication, expert guidance, and a hassle-free experience every step of the way.
            </p>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Expert Legal Team</h3>
                  <p className="text-sm text-gray-300">
                    Our experienced trademark attorneys and specialists have successfully registered thousands of trademarks across all industries.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Comprehensive Search</h3>
                  <p className="text-sm text-gray-300">
                    We conduct thorough federal and state searches to minimize rejection risks and identify potential conflicts before filing.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Fast Processing</h3>
                  <p className="text-sm text-gray-300">
                    Our efficient system ensures quick turnaround times without compromising accuracy or attention to detail.
                  </p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                    <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Ongoing Support</h3>
                  <p className="text-sm text-gray-300">
                    From filing to approval and beyond, we provide continuous support including office action responses and renewal reminders.
                  </p>
                </div>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Expert Legal Team</h3>
              <p className="text-sm text-gray-300">
                Our experienced trademark attorneys and specialists have successfully registered thousands of trademarks across all industries.
              </p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Comprehensive Search</h3>
              <p className="text-sm text-gray-300">
                We conduct thorough federal and state searches to minimize rejection risks and identify potential conflicts before filing.
              </p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Fast Processing</h3>
              <p className="text-sm text-gray-300">
                Our efficient system ensures quick turnaround times without compromising accuracy or attention to detail.
              </p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27]">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Ongoing Support</h3>
              <p className="text-sm text-gray-300">
                From filing to approval and beyond, we provide continuous support including office action responses and renewal reminders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2">TESTIMONIALS</p>
            <h2 className="text-4xl font-bold text-white">
              What Our Clients Say
            </h2>
            <p className="text-gray-300 mt-4">
              Hear from satisfied clients who have protected their brands with our services.
            </p>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={4000}
            >
              {testimonials.slice(0, 3).map((testimonial) => (
                <div key={testimonial.id} className="px-4">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:block">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={3}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={4000}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                  }
                }
              ]}
            >
              {testimonials.slice(0, 3).map((testimonial) => (
                <div key={testimonial.id} className="px-4">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-900 to-[#fd5a27]/95 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#fd5a27]/5"></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2">FAQs</p>
            <h2 className="text-4xl font-bold text-white">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-300 mt-4">
              Find answers to common questions about {service.title.toLowerCase()}.
            </p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {pageData.faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 relative overflow-hidden text-white">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxsZWdhbCUyMHNlcnZpY2VzfGVufDB8fHx8MTc2MzQwMTIxNXww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-[#fd5a27]/90 to-slate-900/95"></div>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Don't wait to secure your brand's future. Start your {service.title.toLowerCase()} application today with our expert team.
          </p>
          
          <Button 
            onClick={handleGetStarted}
            className="bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white px-10 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105"
          >
            Get Started Now
          </Button>
          
          <div className="flex justify-center gap-8 text-sm text-gray-300 mt-8">
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 text-[#fd5a27] mr-2" />
              Expert Guidance
            </div>
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 text-[#fd5a27] mr-2" />
              98% Success Rate
            </div>
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 text-[#fd5a27] mr-2" />
              24/7 Support
            </div>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPackage(null);
        }}
        selectedPackage={selectedPackage}
      />
    </div>
  );
};

export default ServicePage;
